import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { cn } from "cn";
import { Loader2, TriangleAlert } from "lucide-react";
import * as React from "react";
import { Button } from "./button";

// The one v3.2 confirmation (style.css .modal; iOS ConfirmSheet): a bottom
// sheet on phones and a small centred card from 640px. Cancel is the safe
// default and gets focus first; Escape and the scrim cancel.
//
// Two forms:
// - a plain confirm (Delete visit, Discard, Sign out): the confirm button
//   runs `onConfirm` and closes the sheet;
// - a guarded confirm (Delete account): `requireText` asks the user to type
//   a value (their email), or `acknowledgeLabel` to tick a box, before the
//   red button works. The sheet then stays open on confirm; the caller
//   closes it (or shows `message`) when the request ends.
// A plain confirm can also `stayOpen`: the sheet stays up while the request
// runs (confirm spins, Cancel waits) and the caller closes it when it ends,
// so a failure can be retried from the same sheet.

const FIELD =
  "h-11 w-full min-w-0 rounded-xl border-0 bg-v3-surface-2 px-3.5 text-[16px] text-v3-text shadow-v3-hairline outline-hidden transition-[background-color,box-shadow] placeholder:text-v3-text-3 focus:bg-v3-panel focus:ring-2 focus:ring-v3-brand-ring sm:text-[15px]";

const RED = "bg-v3-rec text-white hover:bg-v3-rec hover:opacity-90";

export interface ConfirmSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  /** Red confirm (delete, discard, sign out); otherwise ink. */
  destructive?: boolean;
  /** A request is running: confirm is disabled (and spins when guarded or `stayOpen`). */
  busy?: boolean;
  /**
   * Plain confirm only: keep the sheet open on confirm; the caller closes it
   * (`open` false) when its request ends. Confirm spins and Cancel is
   * disabled while `busy`.
   */
  stayOpen?: boolean;
  onConfirm: () => void;
  /** Opens the sheet (otherwise it is controlled by `open` alone). */
  trigger?: React.ReactNode;
  /** Type-to-confirm: the value to type (compared trimmed, any case). */
  requireText?: string;
  /** The label over the type-to-confirm field. */
  requireTextLabel?: React.ReactNode;
  /** A tick box to confirm instead of typing (when there is nothing to type). */
  acknowledgeLabel?: React.ReactNode;
  /** A status line inside a guarded sheet (e.g. the request failed). */
  message?: React.ReactNode;
  /** Card width from 640px: 380px, or 420px for a guarded sheet. */
  width?: "default" | "wide";
  testId?: string;
  /** Defaults to `${testId}-confirm`. */
  confirmTestId?: string;
}

export function ConfirmSheet({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive = false,
  busy = false,
  stayOpen = false,
  onConfirm,
  trigger,
  requireText,
  requireTextLabel,
  acknowledgeLabel,
  message,
  width,
  testId = "v3-confirm",
  confirmTestId,
}: ConfirmSheetProps): React.JSX.Element {
  const guarded = requireText !== undefined || acknowledgeLabel !== undefined;
  const [typed, setTyped] = React.useState("");
  const [ticked, setTicked] = React.useState(false);
  const fieldId = React.useId();
  // A fresh start each time the sheet opens.
  React.useEffect(() => {
    if (!open) {
      setTyped("");
      setTicked(false);
    }
  }, [open]);
  const confirmed =
    requireText !== undefined
      ? typed.trim().toLowerCase() === requireText.trim().toLowerCase()
      : acknowledgeLabel !== undefined
        ? ticked
        : true;

  const cancel = (
    <AlertDialog.Cancel asChild>
      <Button
        className="max-sm:h-12"
        disabled={(guarded || stayOpen) && busy}
        variant="secondary"
      >
        {cancelLabel}
      </Button>
    </AlertDialog.Cancel>
  );
  const confirmClass = cn("max-sm:h-12", destructive && RED);
  const confirmId = confirmTestId ?? `${testId}-confirm`;

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <AlertDialog.Trigger asChild>{trigger}</AlertDialog.Trigger>}
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <AlertDialog.Content
          className={cn(
            "v3-remap fixed z-50 flex flex-col gap-2 bg-v3-panel font-v3 text-v3-text shadow-v3-pop outline-hidden",
            "max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-h-[92dvh] max-sm:overflow-y-auto max-sm:rounded-t-[22px] max-sm:px-5 max-sm:pt-3 max-sm:pb-[max(1.25rem,env(safe-area-inset-bottom))] max-sm:data-[state=open]:animate-in max-sm:data-[state=open]:slide-in-from-bottom-8",
            "sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[18px] sm:p-6 sm:data-[state=open]:animate-in sm:data-[state=open]:zoom-in-95 sm:data-[state=open]:fade-in-0",
            (width ?? (guarded ? "wide" : "default")) === "wide"
              ? "sm:w-[420px]"
              : "sm:w-[380px]",
          )}
          data-testid={testId}
        >
          <div
            aria-hidden
            className="mx-auto mb-1 h-1 w-9 rounded-full bg-v3-surface-3 sm:hidden"
          />
          <span
            aria-hidden
            className={cn(
              "mb-1 flex size-10 items-center justify-center rounded-full",
              destructive
                ? "bg-v3-rec-tint text-v3-rec-text"
                : "bg-v3-surface-2 text-v3-text-2",
            )}
          >
            <TriangleAlert className="size-5" />
          </span>
          <AlertDialog.Title className="text-[17px] font-semibold leading-6">
            {title}
          </AlertDialog.Title>
          {description ? (
            <AlertDialog.Description className="text-[14px] leading-relaxed text-v3-text-2">
              {description}
            </AlertDialog.Description>
          ) : (
            <AlertDialog.Description className="sr-only">
              {title}
            </AlertDialog.Description>
          )}
          {guarded ? (
            <form
              className="mt-2 flex flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (confirmed && !busy) onConfirm();
              }}
            >
              {requireText !== undefined ? (
                <div>
                  <label
                    className="mb-1.5 block text-[13px] font-semibold text-v3-text-2"
                    htmlFor={fieldId}
                  >
                    {requireTextLabel}
                  </label>
                  <input
                    autoCapitalize="off"
                    autoComplete="off"
                    className={FIELD}
                    data-testid={`${testId}-field`}
                    disabled={busy}
                    id={fieldId}
                    inputMode={requireText.includes("@") ? "email" : undefined}
                    placeholder={requireText}
                    spellCheck={false}
                    value={typed}
                    onChange={(event) => setTyped(event.target.value)}
                  />
                </div>
              ) : (
                <label className="flex min-h-11 items-center gap-3 text-[14px]">
                  <input
                    checked={ticked}
                    className="size-5 accent-v3-rec"
                    disabled={busy}
                    type="checkbox"
                    onChange={(event) => setTicked(event.target.checked)}
                  />
                  {acknowledgeLabel}
                </label>
              )}
              {message}
              <div className="mt-1 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {cancel}
                <Button
                  className={confirmClass}
                  data-testid={confirmId}
                  disabled={!confirmed || busy}
                  type="submit"
                  variant={destructive ? "destructive" : "primary"}
                >
                  {busy && <Loader2 aria-hidden className="animate-spin" />}
                  {confirmLabel}
                </Button>
              </div>
            </form>
          ) : (
            <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              {cancel}
              {stayOpen ? (
                <Button
                  className={confirmClass}
                  data-testid={confirmId}
                  disabled={busy}
                  variant={destructive ? "destructive" : "primary"}
                  onClick={onConfirm}
                >
                  {busy && <Loader2 aria-hidden className="animate-spin" />}
                  {confirmLabel}
                </Button>
              ) : (
                <AlertDialog.Action asChild>
                  <Button
                    className={confirmClass}
                    data-testid={confirmId}
                    disabled={busy}
                    variant={destructive ? "destructive" : "primary"}
                    onClick={onConfirm}
                  >
                    {confirmLabel}
                  </Button>
                </AlertDialog.Action>
              )}
            </div>
          )}
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
